import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, ArrowRight, ShoppingBag, Minus, Plus, ArrowLeft } from 'lucide-react';
import { CartContext } from '../CartContext'; 
import { triggerHaptic } from '../utils/haptics'; 
// [ARCH] Импорт локализации
import { useText } from '../utils/locales';

const Cart = () => {
  const { cartItems, addToCart, decreaseQuantity, clearCart, getCartTotal } = useContext(CartContext);
  const navigate = useNavigate();
  // [ARCH] Хук текстов
  const t = useText();
  
  const total = getCartTotal();

  const handleCheckout = () => {
    triggerHaptic('medium'); 
    navigate('/checkout');   
  };

  const handleClear = () => {
    if (window.confirm('Очистить корзину?')) {
      triggerHaptic('light');
      clearCart();
    }
  };

  const renderModifiers = (modifiers) => {
      if (!modifiers || modifiers.length === 0) return null;
      return (
          <div className="text-xs text-tg-hint mt-1 space-y-0.5">
              {modifiers.map((m, idx) => (
                  // [ARCH] Замена ₽ на {t.currency}
                  <span key={idx} className="block">• {m.name} {m.price > 0 && `(+${m.price}${t.currency})`}</span>
              ))}
          </div>
      );
  };

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-tg-bg flex flex-col items-center justify-center pb-20 p-4">
        <div className="bg-tg-secondary-bg p-8 rounded-full mb-6 shadow-sm">
          <ShoppingBag size={64} className="text-tg-hint opacity-50" />
        </div>
        {/* [ARCH] Тексты из словаря */}
        <h2 className="text-2xl font-bold text-tg-text mb-2">{t.cart.empty}</h2>
        <p className="text-tg-hint text-center mb-8 max-w-[250px]">
          Добавьте товар чтобы сделать заказ
        </p>
        <button 
          onClick={() => navigate('/')}
          className="bg-tg-button text-white px-8 py-4 rounded-2xl font-bold text-lg active:scale-95 transition-transform shadow-lg shadow-tg-button/30"
        >
          Перейти в меню
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-tg-bg p-4 pb-40"> 
      <div className="flex justify-between items-center mb-6 sticky top-0 bg-tg-bg/95 backdrop-blur-sm z-10 -mx-4 px-4 pb-3 pt-tg border-b border-gray-100/5 transition-all">
        <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="text-tg-text active:opacity-60 p-1">
                <ArrowLeft size={24} />
            </button>
            {/* [ARCH] Заголовок из словаря */}
            <h1 className="text-2xl font-bold text-tg-text">{t.cart.title}</h1>
        </div>
        <button 
          onClick={handleClear}
          className="text-red-500 text-sm font-medium flex items-center gap-1 bg-red-500/10 px-3 py-1.5 rounded-lg active:opacity-60"
        >
          <Trash2 size={16} />
          {t.cart.remove || "Очистить"}
        </button>
      </div>

      <div className="space-y-4">
        {cartItems.map((item) => {
            const uniqueKey = item.cartId || item.id;
            const modsPrice = item.selectedModifiers?.reduce((s, m) => s + (m.price || 0), 0) || 0;
            const singlePrice = item.price + modsPrice;

            return (
              <div key={uniqueKey} className="bg-tg-secondary-bg p-3 rounded-2xl flex gap-4 shadow-sm relative overflow-hidden animate-fade-in">
                <div className="w-24 h-24 shrink-0 bg-gray-100 rounded-xl overflow-hidden">
                  <img 
                    src={item.image || 'https://via.placeholder.com/150'} 
                    alt={item.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 flex flex-col justify-between py-1">
                  <div>
                    <h3 className="font-semibold text-tg-text text-[15px] leading-tight line-clamp-2 mb-1">
                      {item.name}
                    </h3>
                    {renderModifiers(item.selectedModifiers)}
                  </div>
                  <div className="flex justify-between items-end mt-2">
                    <span className="font-bold text-tg-text text-lg">
                      {/* [ARCH] Валюта */}
                      {(singlePrice * item.quantity).toFixed(0)} {t.currency}
                    </span>
                    <div className="flex items-center bg-tg-bg rounded-lg border border-gray-100/10 shadow-sm h-8">
                      <button 
                        onClick={() => decreaseQuantity(uniqueKey)}
                        className="w-8 h-full flex items-center justify-center text-tg-hint active:text-red-500 transition-colors"
                      >
                        {item.quantity === 1 ? <Trash2 size={16} /> : <Minus size={16} />}
                      </button>
                      <span className="w-6 text-center text-sm font-bold text-tg-text">
                        {item.quantity}
                      </span>
                      <button 
                        onClick={() => addToCart(item, item.selectedModifiers)}
                        className="w-8 h-full flex items-center justify-center text-tg-button active:scale-90 transition-transform"
                      >
                        <Plus size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
        })}
      </div>

      <div className="fixed bottom-[80px] left-0 right-0 p-4 bg-tg-bg border-t border-gray-100/10 z-30 shadow-top pb-safe">
        <div className="flex justify-between items-center mb-4">
          {/* [ARCH] Текст "Итого" */}
          <span className="text-tg-hint font-medium">{t.cart.total}:</span>
          <span className="font-bold text-tg-text text-2xl">{total} {t.currency}</span>
        </div>
        
        <button 
          onClick={handleCheckout}
          className="w-full bg-tg-button text-white py-4 rounded-2xl font-bold text-lg shadow-lg shadow-tg-button/30 flex justify-center items-center gap-2 active:scale-[0.98] transition-transform"
        >
          {/* [ARCH] Текст кнопки */}
          <span>{t.cart.pay_btn}</span>
          <ArrowRight size={22} />
        </button>
      </div>
    </div>
  );
};

export default Cart;
